import { type RowStatus, StatusPill } from '@/components/Rows'
import { Glyph, type GlyphName } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Header } from '@/ui/core/header'
import { Pressable } from '@/ui/core/pressable'
import { Text } from '@/ui/core/typography'
import { cn } from '@/ui/utils'
import type { ReactNode } from 'react'
import { View } from 'react-native'

export type TileTone = 'white' | 'blush' | 'surface' | 'mint'

/**
 * The title's leading, the second line's, and the gap that reconciles them.
 *
 * Two tiles stand side by side and one of them has a second line. For the pair to read as one
 * object, a two-line title on the left has to occupy exactly what a title and its line occupy on
 * the right: 2 × TITLE == TITLE + GAP + LINE. So the gap is not chosen, it is what is left over —
 * which makes the title's leading the only lever on it, and the reason this is written as the
 * subtraction rather than as its answer.
 *
 * The leading is set solid at the type's own size, which is how a display face wants to be set
 * anyway: these titles are capitals with no descenders, so the seven points between cap height
 * and line is already air. Set looser, the gap left over under the other tile's title grew to
 * ten and read as a stray line of space.
 *
 * Both blocks hang off the foot of their tile, so equal heights put the first lines on one
 * baseline and the last lines on another.
 */
const TITLE_LEADING = 24
const LINE_LEADING = 18
const GAP = TITLE_LEADING - LINE_LEADING

const TONE: Record<TileTone, string> = {
	white: 'bg-white',
	blush: 'bg-blush',
	surface: 'bg-surface',
	mint: 'bg-mint',
}

/**
 * A half-width tile: a glyph on a disc at the top, one big word and one short line pinned to
 * the bottom. The disc is pink on a white tile and white on a coloured one, so it always
 * stands off its ground.
 *
 * The whole tile is the button, and the chevron in the top corner says so — the same mark every
 * row in the app ends with, opposite the glyph rather than after the words, because there is no
 * row here for it to sit at the end of.
 */
export const Tile = ({
	glyph,
	title,
	line,
	status,
	lineNode,
	tone = 'white',
	onPress,
	accessibilityLabel,
}: {
	glyph: GlyphName
	title: string
	line?: string
	/** Draws the line as a status pill rather than as type. */
	status?: RowStatus
	/**
	 * Stands in the line's place, for a state that wants a mark of its own: the kit's coral tag,
	 * which is not a status — it is the app's mark for the one thing waiting on the member, and a
	 * pill would file it with the states that are merely happening.
	 */
	lineNode?: ReactNode
	tone?: TileTone
	onPress: () => void
	accessibilityLabel?: string
}) => (
	<Pressable
		onPress={onPress}
		accessibilityRole="button"
		accessibilityLabel={accessibilityLabel ?? `${title}. ${line ?? ''}`}
		className={cn('min-h-[140px] flex-1 justify-between rounded-note p-4', TONE[tone])}
	>
		<View className="flex-row items-start justify-between">
			<View
				className="h-10 w-10 items-center justify-center rounded-full"
				style={{ backgroundColor: tone === 'white' ? colors.ground : colors.white }}
			>
				<Glyph name={glyph} size={20} color={colors.ink} />
			</View>
			<View className="pt-3">
				<Glyph name="chevron" size={14} color={colors.ink} />
			</View>
		</View>
		<View>
			{/* Two lines, at one size.
			
			    One line and shrink-to-fit meant a two-word title set itself smaller than a
			    one-word title beside it: "Whitening" never shrank and "What's Included" went to
			    eighteen points, so the pair carried two labels at two sizes. Nothing was wrong
			    with either tile on its own, which is why it read as a mistake rather than a
			    choice. Given a second line both titles stay at twenty-four, and the scaling stays
			    on only as the floor for a word longer than either of these. */}
			<Header
				variant="h2"
				numberOfLines={2}
				adjustsFontSizeToFit
				minimumFontScale={0.75}
				className="text-[24px]"
				style={{ lineHeight: TITLE_LEADING }}
			>
				{title}
			</Header>
			{/* A state, not a sentence. "On Its Way" set as a line of body type said the same
			    thing a shipping notice says, in the voice the app uses for an address under a
			    time — so the one tile on this screen that changes week to week looked like the
			    one that never does. As a pill it is the mark this app already puts on a thing
			    with a state, and the tile stops having to change colour to say so. */}
			{lineNode ? (
				<View style={{ marginTop: GAP }}>{lineNode}</View>
			) : (
				!!line &&
				(status ? (
					<View style={{ marginTop: GAP }}>
						<StatusPill status={status} label={line} />
					</View>
				) : (
					<Text size="body1" style={{ marginTop: GAP }}>
						{line}
					</Text>
				))
			)}
		</View>
	</Pressable>
)
